/* eslint-disable react/jsx-props-no-spreading */
// OverflowTooltip using Floating UI - backward compatible with original API
import clsx from 'clsx';
import React, { type ReactNode, useRef, useState } from 'react';
import { type Config } from 'react-popper-tooltip';
import {
  type Placement,
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  safePolygon,
  shift,
  useClick,
  useDismiss,
  useHover,
  useInteractions,
  useRole,
} from '@floating-ui/react';

import type {
  TWLineClampProp,
  TWMaxWidthProp,
  TWPaddingProp,
  TWTextColorProp,
} from '../../types/tailwind';
import { useIsOverflow } from '../../hooks/useIsOverflow';
import { useSafeFloating } from './FloatingUIHelpers';

export const TOOLTIP_CONTAINER_CLASSNAME = 'tooltip-container';

// Convert react-popper-tooltip placement to Floating UI placement
const convertPlacement = (placement?: Config['placement']): Placement => {
  // Most placements are compatible, but handle edge cases
  switch (placement) {
    case 'auto':
      return 'top';
    case 'auto-start':
      return 'top-start';
    case 'auto-end':
      return 'top-end';
    default:
      return (placement || 'top') as Placement;
  }
};

// Keep backward compatibility with original API from dev
type Props<MaxWProp, ColorProp, LineClampProp, PaddingProp> = {
  baseComponent:
    | React.ReactElement<
        React.HTMLAttributes<HTMLTextAreaElement | HTMLSpanElement>
      >
    | string;
  baseComponentIcon?: ReactNode;
  overflowComponent: ReactNode;
  lineClamp?: TWLineClampProp<LineClampProp>;
  color?: TWTextColorProp<ColorProp>;
  padding?: TWPaddingProp<PaddingProp>;
  maxW: TWMaxWidthProp<MaxWProp>;
  placement?: Config['placement'];
  trigger?: 'hover' | 'click';
  transparent?: boolean;
  tooltipRef?: React.RefObject<HTMLDivElement>; // Ignored - Floating UI handles portals
};

/**
 * OverflowTooltip using Floating UI for automatic clipping prevention
 * Backward compatible with original API from dev branch
 */
export const OverflowTooltip = <
  MaxWProp,
  ColorProp,
  LineClampProp,
  PaddingProp
>({
  maxW,
  lineClamp,
  color,
  baseComponent,
  baseComponentIcon,
  overflowComponent,
  padding,
  placement = 'bottom-start',
  trigger = 'hover',
  transparent = false,
  tooltipRef: _, // Ignored - Floating UI handles portals automatically
}: Props<MaxWProp, ColorProp, LineClampProp, PaddingProp>): JSX.Element => {
  const textRef = useRef<HTMLDivElement | null>(null);
  const isOverflow = useIsOverflow(textRef);

  // Use safe Floating UI pattern - demonstrates correct state management for future developers
  const [isOpen, setIsOpen] = useState(false);
  const shouldShow = isOpen && isOverflow && !!overflowComponent;

  // useSafeFloating enforces open + onOpenChange at compile time (prevents common mistakes)
  const { refs, floatingStyles, context } = useSafeFloating({
    open: shouldShow,
    onOpenChange: setIsOpen, // Manage open state, let overflow logic control visibility
    placement: convertPlacement(placement),
    middleware: [
      offset(9), // Match original offset
      flip(),
      shift({ padding: 8 }),
    ],
    whileElementsMounted: autoUpdate,
  });

  const hover = useHover(context, {
    handleClose: safePolygon({
      requireIntent: false,
    }),
    delay: { open: 250 }, // Match original delay
    enabled: trigger === 'hover',
  });
  const click = useClick(context, {
    enabled: trigger === 'click',
  });
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'tooltip' });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    click,
    dismiss,
    role,
  ]);

  const tooltip = (
    <div
      ref={refs.setFloating}
      style={{ ...floatingStyles, zIndex: 1000 }}
      {...getFloatingProps()}
      className={clsx(
        padding,
        TOOLTIP_CONTAINER_CLASSNAME,
        transparent ? 'bg-transparent' : 'border bg-negative border-canvas',
        'flex items-center'
      )}
    >
      {overflowComponent}
    </div>
  );

  return (
    <div
      className="min-w-f0 flex flex-row items-center"
      data-testid="overflow-tooltip"
    >
      {baseComponentIcon}
      <div
        className={clsx(maxW, color, lineClamp, 'truncate')}
        ref={(el) => {
          refs.setReference(el);
          textRef.current = el;
        }}
        {...getReferenceProps()}
      >
        {baseComponent}
      </div>
      {shouldShow && <FloatingPortal>{tooltip}</FloatingPortal>}
    </div>
  );
};
